import differenceInHours from 'date-fns/differenceInHours';
import { rateLimitingEnabled } from '@/utils';
import { includedInPaidPurchaseOrders } from '@/utils/acl';

export default {
  computed: {
    creditLimitReached() {
      if (!this.rateLimitingEnabled) {
        return false;
      }
      return this.questionsRemaining <= 0;
    },
    questionsRemaining() {
      return this.$store.getters['user/rateLimitCredits'];
    },
    rateLimitingEnabled() {
      return rateLimitingEnabled({
        apptimize: this.$apptimize,
        store: this.$store,
        analytics: this.$analytics,
      });
    },
    rateLimitingCycleCredits() {
      return this.$store.getters['app/rateLimitingCycleCredits'];
    },
    rateLimitCycleDays() {
      const appValue = this.$store.getters['app/rateLimitingCycleDay'];

      const apptimizeValue = this.$apptimize.checkExperiments({
        experimentName: 'Rate Limit Cycle Day',
        int: 'rateLimitCycleDay',
        defaultVal: 0,
        matcher: /Rate Limit Cycle Day/,
      });
      if (!isNaN(Number(apptimizeValue)) && Number(apptimizeValue) > 0) {
        return apptimizeValue;
      }

      return appValue;
    },
    shouldShowRateLimit() {
      if (!this.rateLimitingEnabled) {
        return false;
      }

      if (this.isAddonPurchaseOrder) {
        const hasPurchasedAddon = this.$store.getters['purchaseReceipts/active'].some(pr => {
          const po = this.$store.getters['purchaseOrders/get'][pr.purchaseOrderId];
          return po.parentId == this.jsonConfig?.purchaseOrderId && !po.isFree;
        });
        return !hasPurchasedAddon;
      }

      let shouldShow = false;
      try {
        if (this?.$route?.name == 'dashboard#group') {
          // This user is seeing this particular widget within the dashboard-groups context.
          // This context only exists within the catalog context. Because of this the decision to show this widget
          // should be based on the user owning the none free (paid) add on for this particular dashboard.
          const dashboardLocked = this.$store.getters['purchaseReceipts/active'].some( pr => {
            const po = this.$store.getters['purchaseOrders/get'][pr.purchaseOrderId];
            const poId = this.dashboard.unlockedBy.find( id => parseInt(id) == parseInt(pr.purchaseOrderId));
            return !po.isFree && poId;
          });
          shouldShow = !dashboardLocked;
        } else {
          // This user is seeing this widget on the main dashboard context which is the home context to the user
          // If they should see this widget should be based on them owning the main subscription which is the
          // purchase_order of type 'Subscription' or 'Tiered Pricing'
          const hasPaidReceipt = this.$store.getters['purchaseReceipts/active'].some(pr => {
            const po = this.$store.getters['purchaseOrders/get'][pr.purchaseOrderId];
            return !po.isFree && ['Subscription', 'Tiered Pricing'].includes(po.purchaseOrderType);
          });
          shouldShow = !hasPaidReceipt;
        }

        return shouldShow;
      } catch {
        return false;
      }
    },
    showBannerOrCircle() {
      if (this.user?.previewer) {
        return 'banner';
      }
      // if the user is rate limited, we should show the banner or circle but not both
      const includedInNewRateLimitCounterTest = this.$apptimize.checkExperiments({
        experimentName: 'Rate Limit Counter',
        bool: 'NewCounterDesign',
        defaultVal: false,
        matcher: /rate limit counter/gi,
      });

      return includedInNewRateLimitCounterTest ? 'banner' : 'circle';
    },
    user() {
      return this.$store.getters['session/currentUser'];
    },
  },
  methods: {
    checkRateLimitCredits() {
      if (this.rateLimitingEnabled) {
        const shouldAward = this.userNeedsRateLimitCredits();
        if (shouldAward) {
          this.$store.dispatch('user/awardRateLimitCredits', this.rateLimitingCycleCredits);
        }
      }
    },
    flashcardIsRateLimited(flashcard) {
      if (!flashcard?.categoryIds || !this.rateLimitingEnabled) {
        return false;
      }

      const hasPaidAccess = flashcard?.categoryIds.some(cid => {
        const category = this.$store.getters['categories/categories'][cid];
        if (!category) return false;
        return includedInPaidPurchaseOrders(this.$store, category);
      });

      return !hasPaidAccess;
    },
    userNeedsRateLimitCredits() {
      const now = new Date();
      const lastCreditAward = this.$store.getters['user/rateLimitCreditsResetAt'];
      if (lastCreditAward) {
        const hoursSinceAward = differenceInHours(now, lastCreditAward);
        return hoursSinceAward >= (Number(this.rateLimitCycleDays) || 1) * 24;
      }
      return true;
    },
  },
  mounted() {
    this.checkRateLimitCredits();
  },
};
